import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Button } from '@cloudflare/kumo/components/button';
import { ArrowLeft } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import ExhibitorView2025 from '@/components/ExhibitorView2025';
import ExhibitorView2026 from '@/components/ExhibitorView2026';
import ExhibitorEditPanel from '@/components/ExhibitorEditPanel';
import ExhibitorApplicationEditPanel from '@/components/ExhibitorApplicationEditPanel';
import DayStatusPanel from '@/components/DayStatusPanel';

// 編集 UI を出すかどうか。current 年度と一致するレコードのみ。
// (mutation guard で API 側でも 403 が出るが、UI も出展済 2025 では非表示にする)
const CURRENT_EVENT_YEAR_DEFAULT = 2026;

interface ExhibitorDetail {
  id: string;
  applicationNumber: number;
  email: string;
  realName: string;       // 本名 (機密 / 運営連絡用)
  exhibitorName: string;  // 出展者名 (公開・掲示用)
  phone: string;
  website: string | null;
  twitterId: string | null;
  type: string;
  status: string;
  eventYear: number;
  exhibitTitle: string;
  exhibitDescription: string;
  exhibitDays: string[];
  images: string[];
  boothNumber: string | null;
  boothRequirements: string | null;
  specialRequests: string | null;
  deliveryMethods: string[] | null;
  adminNotes: string | null;
  venueFeeTier: string | null;
  boothSizePreference: string;
  isHalfBooth: boolean;
  totalFee: number;
  finalVenueFee: number | null;
  finalEquipmentFee: number | null;
  finalPowerFee: number | null;
  finalTotalFee: number | null;
  isFree: boolean;
  createdAt: string;
  updatedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  paidAt: string | null;
  paymentMethod: string | null;
  yearDetail: unknown;
}

export default function ExhibitorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [exhibitor, setExhibitor] = useState<ExhibitorDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const res = await api.get<ExhibitorDetail>(`/api/admin/exhibitors/${id}`);
      setExhibitor(res.data);
    } catch (err) {
      console.error(err);
      setError('出展者情報の取得に失敗しました');
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    fetchDetail().finally(() => setLoading(false));
  }, [fetchDetail]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader />
      </div>
    );
  }

  if (error || !exhibitor) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/exhibitors" className="self-start">
          <Button variant="ghost" icon={ArrowLeft}>
            一覧へ戻る
          </Button>
        </Link>
        <Banner variant="error">{error ?? '出展者が見つかりません'}</Banner>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Link to="/exhibitors" className="self-start">
        <Button variant="ghost" icon={ArrowLeft}>
          一覧へ戻る
        </Button>
      </Link>

      <div className="flex items-baseline gap-3">
        <h1 className="text-2xl font-semibold text-kumo-default">
          #{exhibitor.applicationNumber} {exhibitor.exhibitorName}
        </h1>
        <Badge variant="outline">{exhibitor.eventYear} 年度</Badge>
        <Badge variant="primary">{exhibitor.status}</Badge>
      </div>

      <Surface className="p-6">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Row label="本名 (運営連絡用)" value={exhibitor.realName} />
          <Row label="メールアドレス" value={exhibitor.email} />
          <Row label="電話番号" value={exhibitor.phone} />
          <Row label="Web" value={exhibitor.website ?? '—'} />
          <Row label="X (Twitter)" value={exhibitor.twitterId ?? '—'} />
          <Row label="ブース番号" value={exhibitor.boothNumber ?? '—'} />
          <Row
            label="出展日"
            value={
              exhibitor.exhibitDays?.length
                ? exhibitor.exhibitDays.map((d) => (d === 'saturday' ? '土曜' : d === 'sunday' ? '日曜' : d)).join('・')
                : '—'
            }
          />
        </div>
        <div className="my-4 h-px bg-kumo-line" />
        <div className="flex flex-col gap-2">
          <span className="text-xs text-kumo-muted">出展タイトル</span>
          <span className="text-base text-kumo-default">{exhibitor.exhibitTitle}</span>
          <span className="text-xs text-kumo-muted">出展内容</span>
          <p className="whitespace-pre-wrap text-sm text-kumo-default">{exhibitor.exhibitDescription}</p>
        </div>
      </Surface>

      {exhibitor.eventYear === 2025 ? (
        <ExhibitorView2025
          exhibitor={exhibitor}
          yearDetail={exhibitor.yearDetail as Year2025Detail | null}
        />
      ) : (
        <ExhibitorView2026
          exhibitor={exhibitor}
          yearDetail={exhibitor.yearDetail as Year2026Detail | null}
        />
      )}

      {exhibitor.eventYear === CURRENT_EVENT_YEAR_DEFAULT ? (
        <>
          <ExhibitorApplicationEditPanel
            exhibitor={exhibitor}
            yearDetail={exhibitor.yearDetail as Year2026Detail | null}
            onUpdate={fetchDetail}
          />
          <DayStatusPanel exhibitorId={exhibitor.id} exhibitDays={exhibitor.exhibitDays ?? []} />
          <ExhibitorEditPanel exhibitor={exhibitor} onUpdate={fetchDetail} />
        </>
      ) : exhibitor.adminNotes ? (
        <Surface className="p-4">
          <div className="text-xs text-kumo-muted">管理者メモ</div>
          <p className="whitespace-pre-wrap text-sm text-kumo-default">{exhibitor.adminNotes}</p>
        </Surface>
      ) : null}
    </div>
  );
}

interface Year2025Detail {
  exhibitorId: string;
  needsTable: boolean;
  needsChair: boolean;
  additionalTables: number;
  additionalChairs: number;
  needsPower: boolean;
  powerCapacity: string | null;
  customPowerCapacity: number | null;
  equipmentFee: number;
  powerFee: number;
}

interface Year2026Detail {
  exhibitorId: string;
  tableCount: number;
  chairCount: number;
  powerWatts: number;
  sponsorUnits: number;
  sponsorDisplayName: string | null;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-kumo-muted">{label}</span>
      <span className="text-sm text-kumo-default">{value}</span>
    </div>
  );
}
